import { Injectable } from '@angular/core';
import { InertBodyHelper } from './inert-body-helper';
import { SanitizingSvgSerializer } from './sanitizing-svg-serializer';

/**
 * SvgSanitizer is based on angular html_sanitizer
 * https://github.com/angular/angular/blob/4456e7e4dec87f237cee0ed2eb6eb4431e806ea5/packages/core/src/sanitization/html_sanitizer.ts.
 */
@Injectable({
  providedIn: 'root'
})
export class SvgSanitizer {
  private inertBodyHelper: InertBodyHelper;
  /**
   * Sanitizes the given unsafe, untrusted HTML fragment, and returns HTML text that is safe to add to
   * the DOM in a browser environment.
   */
  sanitize(defaultDoc: Document, unsafeHtmlInput: string): string {
    let inertBodyElement: HTMLElement|null = null;
    try {
      if (this.inertBodyHelper === undefined) {
        this.inertBodyHelper = new InertBodyHelper(defaultDoc);
      }
      // Make sure unsafeHtml is actually a string (TypeScript types are not enforced at runtime).
      let unsafeHtml = unsafeHtmlInput !== undefined ? String(unsafeHtmlInput) : '';
      inertBodyElement = this.inertBodyHelper.getInertBodyElement(unsafeHtml);

      // mXSS protection. Repeatedly parse the document to make sure it stabilizes, so that a browser
      // trying to auto-correct incorrect HTML cannot cause formerly inert HTML to become dangerous.
      let mXSSAttempts = 5;
      let parsedHtml = unsafeHtml;

      do {
        if (mXSSAttempts === 0) {
          throw new Error('Failed to sanitize html because the input is unstable');
        }
        mXSSAttempts--;

        unsafeHtml = parsedHtml;
        parsedHtml = inertBodyElement.innerHTML;
        inertBodyElement = this.inertBodyHelper.getInertBodyElement(unsafeHtml);
      } while (unsafeHtml !== parsedHtml);

      const sanitizer = new SanitizingSvgSerializer();
      let element = this.getTemplateContent(inertBodyElement);
      if (element === null) {
        element = inertBodyElement;
      }
      return sanitizer.sanitizeChildren(element as Element);
    } finally {
      // In case anything goes wrong, clear out inertElement to reset the entire DOM structure.
      if (inertBodyElement !== null) {
        let parent = this.getTemplateContent(inertBodyElement);
        if (parent === null) {
          parent = inertBodyElement;
        }
        while (parent.firstChild !== null) {
          parent.removeChild(parent.firstChild);
        }
      }
    }
  }

  private getTemplateContent(el: Node): Node|null {
    return 'content' in el && this.isTemplateElement(el) ?
      el.content :
      null;
  }

  private isTemplateElement(el: Node): el is HTMLTemplateElement {
    return el.nodeType === Node.ELEMENT_NODE && el.nodeName === 'TEMPLATE';
  }
}
